import React, { memo, useRef, useMemo, Suspense, useCallback } from 'react'
import Image from 'next/image'
import dynamic from 'next/dynamic'
import { audioExtensions, videoFileExts, image3DExtensions } from 'utils/constants'

// Dynamically import heavy components
const ImageViewMenu = dynamic(() => import('components/ImageViewMenu'), { ssr: false })
const Viewer3D = dynamic(() => import('components/Viewer3D'), {
  ssr: false,
  loading: () => <div className="w-full h-[250px] bg-gray-200 rounded animate-pulse" />,
})
const JsonViewer = dynamic(() => import('components/JsonViewer'), {
  ssr: false,
  loading: () => <div className="w-[300px] h-[250px] bg-gray-200 rounded animate-pulse" />,
})

// Preload common image sizes
const imageSizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'

// Common placeholder for images
const blurDataURL =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN8/+F9PQAIHwQFu7X7WgAAAABJRU5ErkJggg=='

const MediaItem = memo(({ item, index, handleMediaClick, handleImageDownload, handleUrlCopy, onDeleteClick }) => {
  const fileType = useMemo(() => item.url.split('.').pop().toLowerCase(), [item.url])
  const audioRef = useRef(null)
  const videoRef = useRef(null)

  // Memoized event handlers
  const onMediaClick = useCallback(() => handleMediaClick(item), [handleMediaClick, item])
  const onDownloadClick = useCallback(
    (e) => {
      e.stopPropagation()
      handleImageDownload(item.url)
    },
    [handleImageDownload, item.url]
  )
  const onCopyClick = useCallback(
    (e) => {
      e.stopPropagation()
      handleUrlCopy(item.url)
    },
    [handleUrlCopy, item.url]
  )
  const onDelete = useCallback(
    (e) => {
      e.stopPropagation()
      onDeleteClick(item.id)
    },
    [onDeleteClick, item.id]
  )

  // Video/audio event handlers
  const handleMouseOver = useCallback((e) => e.target.play(), [])
  const handleMouseOut = useCallback((e) => e.target.pause(), [])

  const renderContent = useMemo(() => {
    if (audioExtensions.includes(fileType)) {
      return (
        <div>
          <audio
            preload="metadata"
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            controls
            className="w-full"
            ref={audioRef}
            src={item.url}
          />
        </div>
      )
    } else if (videoFileExts.includes(fileType)) {
      return (
        <video
          preload="metadata"
          muted
          playsInline
          onMouseOver={handleMouseOver}
          onMouseOut={handleMouseOut}
          ref={videoRef}
          src={item.url}
          className="w-full rounded"
        />
      )
    } else if (image3DExtensions.includes(fileType)) {
      return (
        <Suspense fallback={<div className="w-full h-[250px] bg-gray-200 rounded animate-pulse" />}>
          <Viewer3D file={item.url} classNames="img rounded group" />
        </Suspense>
      )
    } else if (fileType === 'json') {
      return (
        <Suspense fallback={<div className="w-[300px] h-[250px] bg-gray-200 rounded animate-pulse" />}>
          <JsonViewer data={item.url} isLink={true} classNames="img w-[300px] h-[250px] rounded group" />
        </Suspense>
      )
    } else {
      return (
        <Image
          src={item.url}
          alt={`Media ${index}`}
          width={300}
          height={250}
          sizes={imageSizes}
          className="object-cover w-full rounded img group"
          loading={index < 3 ? 'eager' : 'lazy'}
          priority={index < 3}
          quality={75}
          placeholder="blur"
          blurDataURL={blurDataURL}
        />
      )
    }
  }, [fileType, item.url, index, handleMouseOver, handleMouseOut])

  return (
    <div className="flex relative justify-center items-center rounded-sm cursor-pointer group">
      <div onClick={onMediaClick} className="img w-full rounded z-[9]">
        {renderContent}
        {!audioExtensions.includes(fileType) && (
          <ImageViewMenu
            className="!top-2 !py-1 !px-2 !gap-1 !hidden group-hover:!flex"
            handleImageDownload={onDownloadClick}
            handleUrlCopy={onCopyClick}
          />
        )}
      </div>
      {item.type === 'uploaded' && (
        <div
          className="cursor-pointer absolute top-3 right-3 z-[99] hidden group-hover:block bg-slate-200 rounded-full p-1"
          onClick={onDelete}
        >
          <Image src="/assets/common/close.svg" width={16} height={16} alt="Delete" loading="lazy" />
        </div>
      )}
    </div>
  )
})

MediaItem.displayName = 'MediaItem'

export default MediaItem
